import React from 'react';
import { CheckCircle2, Clock, XCircle, RefreshCw, MessageSquare } from 'lucide-react';

interface WorkLog {
  id: string;
  editor_id: string;
  request_id: string | null;
  request_type: string;
  vertical: string | null;
  title: string | null;
  assigned_creatives: number;
  completed_creatives: number;
  status: string;
  started_at: string;
  last_activity_at?: string;
  last_note_at?: string | null;
  completed_at: string | null;
  date_bucket: string;
  editor_name: string;
  editor_display_name: string;
  notes_count: number;
}

interface Props {
  log: WorkLog;
  onClick: () => void;
}

function getStatusStyle(status: string) {
  switch (status) {
    case 'completed': return { bg: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300', Icon: CheckCircle2 };
    case 'reassigned': return { bg: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400', Icon: RefreshCw };
    case 'cancelled': return { bg: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300', Icon: XCircle };
    default: return { bg: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300', Icon: Clock };
  }
}

function getProgressBarColor(status: string, completed: number, assigned: number) {
  if (status === 'completed') return 'bg-emerald-500';
  if (completed > 0 && assigned > 0) return 'bg-amber-400';
  return 'bg-rose-400';
}

export function TrackingLogCard({ log, onClick }: Props) {
  const progress = log.assigned_creatives > 0
    ? Math.min(Math.round((log.completed_creatives / log.assigned_creatives) * 100), 100)
    : 0;

  const { bg, Icon } = getStatusStyle(log.status);

  return (
    <div
      onClick={onClick}
      className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 cursor-pointer transition bg-white dark:bg-gray-800"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {log.title || 'Untitled'}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {log.editor_display_name || log.editor_name}
            {log.vertical && ` \u2022 ${log.vertical}`}
          </p>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${bg}`}>
          <Icon size={10} />
          {log.status}
        </span>
      </div>

      {/* Progress bar for non-manual entries */}
      {log.request_type !== 'manual' && log.assigned_creatives > 0 && (
        <div className="mt-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] text-gray-400">
              {log.completed_creatives}/{log.assigned_creatives} creatives
            </span>
            <span className="text-[10px] text-gray-400">{progress}%</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
            <div
              className={`h-1.5 rounded-full transition-all ${getProgressBarColor(log.status, log.completed_creatives, log.assigned_creatives)}`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Type badge + notes count */}
      <div className="flex items-center gap-2 mt-2">
        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
          log.request_type === 'manual'
            ? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
            : log.request_type === 'launch_request'
            ? 'bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400'
            : 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
        }`}>
          {log.request_type === 'manual' ? 'Manual' : log.request_type === 'launch_request' ? 'Launch' : 'File Req'}
        </span>
        {log.notes_count > 0 && (
          <span className="flex items-center gap-0.5 text-[10px] text-gray-400">
            <MessageSquare size={10} />
            {log.notes_count}
          </span>
        )}
      </div>
    </div>
  );
}
